import { useTranslation } from '@rhino-project/core/i18n';
import { compact, join } from 'lodash';
import {
  Button,
  Card,
  Typography,
  Dropdown,
  DropdownPopover,
  DropdownMenu,
  DropdownItem
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useParams } from '@tanstack/react-router';
import { useModelShow } from '@rhino-project/core/hooks';
import { BaseAuthedPage, MaxWidth } from '@rhino-project/ui-heroui';
import { chipAccent } from '../../components/shared/PlayerChips';
import {
  defaultCountries,
  parseCountry,
  FlagImage
} from 'react-international-phone';
import { useExternalPlayerAdd } from '../../hooks/externalplayer';
import TeamHistoryItem from '../../components/shared/TeamHistoryItem';

const parsedCountries = defaultCountries.map((c) => parseCountry(c));

type ReadOnlyChipProps = {
  value: string | number | null | undefined;
  label?: string;
};

const ReadOnlyChip = ({ value, label }: ReadOnlyChipProps) => {
  if (!value) return null;
  return (
    <span className={chipAccent}>{label ? `${label} ${value}` : value}</span>
  );
};

type CountryReadOnlyChipProps = {
  country: string | null | undefined;
};

const CountryReadOnlyChip = ({ country }: CountryReadOnlyChipProps) => {
  const { locale } = useTranslation();
  if (!country) return null;
  const c = parsedCountries.find((p) => p.iso2 === country.toLowerCase());
  return (
    <span className={`${chipAccent} gap-1.5`}>
      {c && <FlagImage className="size-4" iso2={c.iso2} />}
      {c
        ? new Intl.DisplayNames([locale.replaceAll('_', '-')], {
            type: 'region'
          }).of(c.iso2.toUpperCase())
        : country}
    </span>
  );
};

// Rhino adds display_name at runtime; cast to access computed fields
const DatabasePlayerPage = () => {
  const { t } = useTranslation();

  const { id: playerId } = useParams({ strict: false });
  const { resource: player } = useModelShow('external_player', playerId!);

  const { memberActions, handleAdd } = useExternalPlayerAdd(playerId);

  const height = player?.height_feet
    ? compact([player.height_feet, player.height_inches]).join("'")
    : null;

  return (
    <BaseAuthedPage>
      <MaxWidth>
        <div className="flex flex-row justify-between items-start">
          <div className="flex flex-col gap-2">
            <Typography.Heading level={1} className="m-0">
              {join(compact([player?.first_name, player?.last_name]), ' ')}
              {player?.number && ` #${player.number}`}
            </Typography.Heading>
            <div className="flex flex-wrap gap-1.5 items-center">
              <ReadOnlyChip value={player?.position?.display_name} />
              <ReadOnlyChip
                value={
                  player?.hand === 'L'
                    ? t('frontend.messages.left')
                    : player?.hand === 'R'
                      ? t('frontend.messages.right')
                      : null
                }
                label={t('frontend.messages.hand')}
              />
              <ReadOnlyChip
                value={player?.dob_year}
                label={t('frontend.messages.born')}
              />
              <ReadOnlyChip
                value={player?.grad_year}
                label={t('frontend.messages.grad')}
              />
              <ReadOnlyChip
                value={height}
                label={t('frontend.messages.height')}
              />
              <ReadOnlyChip
                value={player?.weight_lbs ? `${player.weight_lbs}` : null}
                label={t('frontend.messages.weight')}
              />
              <ReadOnlyChip
                value={player?.hometown}
                label={t('frontend.messages.hometown')}
              />
              <CountryReadOnlyChip country={player?.country} />
              <ReadOnlyChip
                value={player?.commitment}
                label={t('frontend.messages.commitment')}
              />
            </div>
          </div>
          {memberActions && (
            <Dropdown>
              <Button variant="secondary">
                {t('frontend.messages.add')}
                <Icon icon="bi:caret-down-fill" />
              </Button>
              <DropdownPopover>
                <DropdownMenu
                  aria-label={t('frontend.messages.add_player_actions')}
                >
                  {memberActions.map((a) => (
                    <DropdownItem
                      key={a.name}
                      id={a.name}
                      textValue={a.label}
                      onAction={() => handleAdd(a.name)}
                    >
                      {a.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
          )}
        </div>

        {(player?.external_rosters?.length ?? 0) > 0 &&
          player?.external_rosters && (
            <Card className="my-4">
              <Card.Header>
                <Card.Title>{t('frontend.messages.team_history')}</Card.Title>
              </Card.Header>
              <Card.Content>
                {player.external_rosters.map((roster) => (
                  <TeamHistoryItem
                    key={roster.id}
                    seasonName={
                      roster.external_team_season?.season?.name ?? undefined
                    }
                    teamName={
                      roster.external_team_season?.external_team?.display_name
                    }
                  />
                ))}
              </Card.Content>
            </Card>
          )}
      </MaxWidth>
    </BaseAuthedPage>
  );
};

export default DatabasePlayerPage;
